import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getPortalContext,
  initiatePayment,
  getPaymentStatus,
  type PortalContext,
  type InitiateResponse,
  type PaymentStatus,
} from "./api.ts";

type Stage =
  | { kind: "select" }
  | { kind: "phone"; packageId: string }
  | { kind: "paying"; initiate: InitiateResponse; phone: string }
  | { kind: "success"; status: PaymentStatus; phone: string }
  | { kind: "failed"; reason: string; phone?: string };

export function App() {
  // routerId comes from the captive portal redirect URL: ?router=<uuid>.
  // macAddress may also be appended by some MikroTik configs.
  const params = new URLSearchParams(window.location.search);
  const routerId = params.get("router") ?? "";
  const macAddress = params.get("mac") ?? undefined;

  const ctx = useQuery({
    queryKey: ["portal-ctx", routerId],
    queryFn: () => getPortalContext(routerId),
    enabled: routerId.length > 0,
    retry: 0,
  });

  const [stage, setStage] = useState<Stage>({ kind: "select" });

  if (!routerId) {
    return <Shell><BadEntry message="No router specified in the URL." /></Shell>;
  }
  if (ctx.isLoading) {
    return <Shell><Loading /></Shell>;
  }
  if (ctx.error || !ctx.data) {
    return (
      <Shell>
        <BadEntry message={ctx.error?.message ?? "Could not load this hotspot."} />
      </Shell>
    );
  }

  const data = ctx.data;

  return (
    <Shell tenantName={data.tenant.name}>
      {stage.kind === "select" && (
        <PackageSelect
          context={data}
          onPick={(packageId) => setStage({ kind: "phone", packageId })}
        />
      )}
      {stage.kind === "phone" && (
        <PhoneEntry
          context={data}
          packageId={stage.packageId}
          macAddress={macAddress}
          onCancel={() => setStage({ kind: "select" })}
          onInitiated={(initiate, phone) =>
            setStage({ kind: "paying", initiate, phone })
          }
          onFailed={(reason, phone) =>
            setStage({ kind: "failed", reason, phone })
          }
        />
      )}
      {stage.kind === "paying" && (
        <Paying
          initiate={stage.initiate}
          phone={stage.phone}
          onSuccess={(status) =>
            setStage({ kind: "success", status, phone: stage.phone })
          }
          onFailed={(reason) =>
            setStage({ kind: "failed", reason, phone: stage.phone })
          }
        />
      )}
      {stage.kind === "success" && (
        <Success status={stage.status} phone={stage.phone} />
      )}
      {stage.kind === "failed" && (
        <Failed
          reason={stage.reason}
          onRetry={() => setStage({ kind: "select" })}
        />
      )}
    </Shell>
  );
}

function Shell({
  tenantName,
  children,
}: {
  tenantName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <div className="mx-auto max-w-md px-4 py-8">
        <div className="mb-6 text-center">
          <div className="mx-auto grid h-10 w-10 place-items-center rounded-lg bg-brand-500 text-white">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <path d="M6 14l4-4 4 4 4-6 4 4" />
            </svg>
          </div>
          {tenantName ? (
            <>
              <p className="mt-3 text-xs uppercase tracking-wide text-slate-500">
                {tenantName}
              </p>
              <p className="mt-0.5 text-lg font-semibold text-slate-900">
                WiFi access
              </p>
            </>
          ) : (
            <p className="mt-3 text-lg font-semibold text-slate-900">SpotMesh</p>
          )}
        </div>
        {children}
        <p className="mt-8 text-center text-xs text-slate-400">
          Powered by SpotMesh
        </p>
      </div>
    </div>
  );
}

function PackageSelect({
  context,
  onPick,
}: {
  context: PortalContext;
  onPick: (packageId: string) => void;
}) {
  if (context.packages.length === 0) {
    return (
      <Card>
        <p className="text-sm text-slate-600">
          No packages are available right now. Please ask the operator.
        </p>
      </Card>
    );
  }
  return (
    <Card>
      <p className="text-sm font-medium text-slate-700">Pick a package</p>
      <div className="mt-4 space-y-2">
        {context.packages.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p.id)}
            className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left hover:border-brand-500 hover:bg-brand-50"
          >
            <div>
              <p className="font-medium text-slate-900">{p.name}</p>
              <p className="text-xs text-slate-500">
                {formatDuration(p.durationMinutes)}
              </p>
            </div>
            <p className="text-base font-semibold text-brand-700">
              KES {p.priceKes.toLocaleString()}
            </p>
          </button>
        ))}
      </div>
    </Card>
  );
}

function PhoneEntry({
  context,
  packageId,
  macAddress,
  onCancel,
  onInitiated,
  onFailed,
}: {
  context: PortalContext;
  packageId: string;
  macAddress?: string;
  onCancel: () => void;
  onInitiated: (r: InitiateResponse, phone: string) => void;
  onFailed: (reason: string, phone: string) => void;
}) {
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pkg = context.packages.find((p) => p.id === packageId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const r = await initiatePayment({
        tenantId: context.tenant.id,
        routerId: context.router.id,
        packageId,
        phone,
        macAddress,
      });
      onInitiated(r, phone);
    } catch (e2) {
      const msg = e2 instanceof Error ? e2.message : "Could not start payment";
      setErr(msg);
      // Most errors are recoverable (e.g. invalid phone) — stay on this page;
      // only escalate to the failed screen for explicit gateway errors.
      if (msg.includes("502") || msg.includes("503")) onFailed(msg, phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <div className="mb-4 rounded-md bg-slate-50 p-3 text-sm">
        <p className="text-slate-600">
          {pkg?.name} &middot; {pkg ? formatDuration(pkg.durationMinutes) : ""}
        </p>
        <p className="mt-0.5 font-semibold text-slate-900">
          KES {pkg?.priceKes.toLocaleString()}
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-700">M-Pesa phone number</span>
          <input
            inputMode="tel"
            autoComplete="tel"
            placeholder="0712 345 678"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-base focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>
        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={submitting || !phone}
            className="flex-1 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Pay with M-Pesa"}
          </button>
        </div>
      </form>
    </Card>
  );
}

function Paying({
  initiate,
  phone,
  onSuccess,
  onFailed,
}: {
  initiate: InitiateResponse;
  phone: string;
  onSuccess: (s: PaymentStatus) => void;
  onFailed: (reason: string) => void;
}) {
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // 60 * 2s = 2 minutes

    const tick = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const status = await getPaymentStatus(initiate.paymentId);
        if (cancelled) return;
        if (status.payment.status === "success") {
          onSuccess(status);
          return;
        }
        if (status.payment.status === "failed") {
          onFailed(status.payment.darajaResultDesc ?? "Payment failed");
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          onFailed("Timed out waiting for M-Pesa confirmation");
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        if (cancelled) return;
        // Transient network error — keep polling
        setTimeout(tick, 2500);
      }
    };
    setTimeout(tick, 1500);
    return () => {
      cancelled = true;
    };
  }, [initiate.paymentId, onFailed, onSuccess]);

  return (
    <Card>
      <div className="text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
        <p className="mt-4 text-sm font-medium text-slate-900">
          Check your phone
        </p>
        <p className="mt-1 text-xs text-slate-500">
          We sent an M-Pesa prompt to {phone}. Enter your PIN to complete.
        </p>
      </div>
    </Card>
  );
}

function Success({
  status,
  phone,
}: {
  status: PaymentStatus;
  phone: string;
}) {
  return (
    <Card tone="success">
      <div className="text-center">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-brand-100 text-brand-700">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
            <path d="M5 12l5 5L20 7" />
          </svg>
        </div>
        <p className="mt-4 text-lg font-semibold text-slate-900">You're online</p>
        <p className="mt-1 text-sm text-slate-600">
          Payment confirmed from {phone}.
        </p>
        {status.payment.darajaMpesaReceipt && (
          <p className="mt-3 inline-block rounded-full bg-slate-100 px-3 py-1 text-xs font-mono text-slate-700">
            {status.payment.darajaMpesaReceipt}
          </p>
        )}
        {status.voucher?.expiresAt && (
          <p className="mt-3 text-xs text-slate-500">
            Valid until {new Date(status.voucher.expiresAt).toLocaleString()}
          </p>
        )}
      </div>
    </Card>
  );
}

function Failed({ reason, onRetry }: { reason: string; onRetry: () => void }) {
  return (
    <Card tone="error">
      <p className="text-sm font-medium text-slate-900">Payment didn't go through</p>
      <p className="mt-1 text-xs text-slate-600">{reason}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
      >
        Try again
      </button>
    </Card>
  );
}

function Loading() {
  return (
    <Card>
      <div className="grid place-items-center py-6 text-sm text-slate-500">
        Loading…
      </div>
    </Card>
  );
}

function BadEntry({ message }: { message: string }) {
  return (
    <Card tone="error">
      <p className="text-sm font-medium text-slate-900">Can't show this page</p>
      <p className="mt-1 text-xs text-slate-600">{message}</p>
    </Card>
  );
}

function Card({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: "success" | "error";
}) {
  const ring =
    tone === "success"
      ? "ring-brand-200"
      : tone === "error"
        ? "ring-red-200"
        : "ring-slate-200";
  return (
    <div className={`rounded-2xl bg-white p-5 shadow-sm ring-1 ${ring}`}>
      {children}
    </div>
  );
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} minutes`;
  if (mins < 1440) return `${mins / 60} hour${mins === 60 ? "" : "s"}`;
  if (mins < 10080) return `${mins / 1440} day${mins === 1440 ? "" : "s"}`;
  if (mins < 43200) return `${mins / 10080} week${mins === 10080 ? "" : "s"}`;
  return `${mins / 43200} month${mins === 43200 ? "" : "s"}`;
}
